import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { mergeGuestFavorites } from '@/lib/guestFavorites';
import { clearPushRegistration } from '@/lib/pushNotifications';
import type { ProfileRow } from '@/types/database';

interface Ctx {
  session: Session | null;
  user: User | null;
  profile: ProfileRow | null;
  loading: boolean;
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
}
const C = createContext<Ctx>({} as Ctx);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<ProfileRow | null>(null);
  const [loading, setLoading] = useState(true);
  // Dédoublonne les chargements concurrents (StrictMode + onAuthStateChange double-fires)
  const inflight = useRef<{ uid: string; promise: Promise<void> } | null>(null);
  const lastLoadedUid = useRef<string | null>(null);

  const loadProfile = useCallback(async (uid: string, force = false) => {
    if (!force && lastLoadedUid.current === uid) return;
    // Un refresh forcé (force=true) NE réutilise PAS une lecture en cours : il
    // suit une écriture (ex. changement de lieu) et doit relire la valeur
    // fraîche. Sinon on renverrait la promesse d'un chargement antérieur au
    // write → profil périmé et UI figée sur l'ancien lieu.
    if (!force && inflight.current?.uid === uid) return inflight.current.promise;
    const entry = { uid } as { uid: string; promise: Promise<void> };
    entry.promise = (async () => {
      try {
        const { data, error } = await supabase.from('profiles').select('*').eq('id', uid).maybeSingle();
        if (error) throw error;
        lastLoadedUid.current = uid;
        setProfile(data ?? null);
      } catch (e) {
        console.error('[auth] loadProfile failed', e);
      } finally {
        // Ne nettoie que SA propre entrée (identité) : un refresh forcé
        // concurrent a pu remplacer inflight.current entre-temps.
        if (inflight.current === entry) inflight.current = null;
      }
    })();
    inflight.current = entry;
    return entry.promise;
  }, []);

  useEffect(() => {
    let cancelled = false;
    const handle = async (sess: Session | null) => {
      if (cancelled) return;
      setSession(sess);
      if (sess?.user) {
        // Rapatrie les favoris ajoutés hors connexion (no-op si le stockage
        // local est vide — donc inoffensif sur les TOKEN_REFRESHED répétés).
        void mergeGuestFavorites(sess.user.id).catch(() => {});
        await loadProfile(sess.user.id);
      } else {
        lastLoadedUid.current = null;
        setProfile(null);
      }
      if (!cancelled) setLoading(false);
    };
    // Récupère immédiatement la session courante (au cas où INITIAL_SESSION a déjà été émis avant le subscribe)
    supabase.auth.getSession().then(({ data }) => handle(data.session)).catch((e) => {
      console.error('[auth] getSession failed', e);
      if (!cancelled) setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, sess) => { void handle(sess); });
    // Filet de sécurité : débloque l'UI au pire en 4s
    const t = setTimeout(() => { if (!cancelled) setLoading(false); }, 4000);
    return () => { cancelled = true; clearTimeout(t); sub.subscription.unsubscribe(); };
  }, [loadProfile]);

  const value = useMemo<Ctx>(() => ({
    session,
    user: session?.user ?? null,
    profile,
    loading,
    refreshProfile: async () => { if (session?.user) await loadProfile(session.user.id, true); },
    signOut: async () => {
      await clearPushRegistration(session?.user?.id ?? null);
      await supabase.auth.signOut();
    },
  }), [session, profile, loading, loadProfile]);

  return <C.Provider value={value}>{children}</C.Provider>;
}

export const useAuth = () => useContext(C);
